<script setup lang="ts">
import { computed } from 'vue';
import { useNxSocketStatus } from '@blpde/connect';
import { useShellUser } from '@/auth';

// Initials avatar + connection-status dot, rendered as the user menu's "icon"
// in the navbar (ported from the old AppRail). Sources user + socket state
// itself so it can be dropped straight into a NavBarItem's `icon` slot.
const { user } = useShellUser();
const { status } = useNxSocketStatus();

function initialsFor(name: string, email: string): string {
  const src = (name || email || '').trim();
  if (!src) return '?';
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  }
  const local = email.split('@')[0] || '';
  return local.slice(0, 2).toUpperCase() || '?';
}

const initials = computed(() => initialsFor(user.value?.name ?? '', user.value?.email ?? ''));
</script>

<template>
  <span class="cx-avatar">
    {{ initials }}
    <span class="cx-avatar-dot" :class="`is-${status}`" />
  </span>
</template>

<style scoped>
.cx-avatar {
  position: relative;
  display: inline-grid;
  place-items: center;
  /* Keep ≤ the 20px nav icons' trigger height so the avatar doesn't make the
     whole navbar taller than its 54px baseline. */
  width: var(--nxt-avatar-size-sm);
  height: var(--nxt-avatar-size-sm);
  border-radius: 50%;
  background: var(--nxt-surface-bg);
  color: var(--nxt-brand-bg);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-semibold);
  font-family: inherit;
  line-height: 1;
  user-select: none;
}

.cx-avatar-dot {
  position: absolute;
  right: calc(-1 * (var(--nxt-space-2xs) / 2));
  bottom: calc(-1 * (var(--nxt-space-2xs) / 2));
  width: var(--nxt-dot-size-md);
  height: var(--nxt-dot-size-md);
  border-radius: 50%;
  /* Border in the navbar's own colour so the dot reads as a notch on the avatar. */
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-bg);
  background: var(--nxt-neutral-bg); /* unknown / idle */
}
.cx-avatar-dot.is-ready {
  background: var(--nxt-success-bg); /* connected */
}
.cx-avatar-dot.is-connecting {
  background: var(--nxt-warning-bg); /* connecting */
}
.cx-avatar-dot.is-disconnected {
  background: var(--nxt-danger-bg); /* disconnected */
}
</style>
